import * as React from 'react'

import type { IconNode, LucideIcon, LucideProps } from 'lucide-react'
import { Icon } from 'lucide-react'
import { observer } from 'mobx-react'

import {
  LIBRARY_GLYPHS,
  type LibraryGlyph,
} from '~/client/src/shared/enums/SitemapAttributeIcon'

import { PictogramArtGate } from './pictograms'
import { createShardedArt } from './shardedArt'

// No location type draws a library glyph by default, so their art loads on first read, company
// logos apart from the rest. Until it lands a glyph draws nothing.
const art = createShardedArt({
  names: LIBRARY_GLYPHS,
  shardOf: (name: LibraryGlyph) =>
    name.startsWith('brand-') ? 'brands' : 'library',
  loaders: {
    brands: () => import('./libraryGlyphs/brands.generated'),
    library: () => import('./libraryGlyphs/library.generated'),
  },
  missing: [],
})

const NO_NODES: IconNode = []

/** A library glyph's geometry, or nothing while it loads. */
export const libraryGlyphNodes = (name: LibraryGlyph): IconNode =>
  art.read(name) ?? NO_NODES

export const isLibraryGlyphArtLoaded = art.isLoaded

export const hasPendingLibraryGlyphArt = art.hasPending

export const libraryGlyphArtSettled = art.settled

const libraryGlyphIcon = (name: LibraryGlyph): LucideIcon =>
  observer(
    React.forwardRef<SVGSVGElement, LucideProps>((props, ref) =>
      React.createElement(Icon, {
        icon: {
          name,
          node: React.useContext(PictogramArtGate)
            ? libraryGlyphNodes(name)
            : (art.peek(name) ?? NO_NODES),
        },
        ...props,
        ref,
      }),
    ),
  ) as LucideIcon

export const LIBRARY_GLYPH_ICONS = Object.fromEntries(
  LIBRARY_GLYPHS.map(name => [name, libraryGlyphIcon(name)]),
) as Record<LibraryGlyph, LucideIcon>
